import React, { useEffect, useState } from 'react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface RenameFolderDialogProps {
  open: boolean;
  currentName: string;
  onClose: () => void;
  onRename: (name: string) => Promise<void> | void;
  isSubmitting?: boolean;
}

export const RenameFolderDialog: React.FC<RenameFolderDialogProps> = ({
  open,
  currentName,
  onClose,
  onRename,
  isSubmitting = false,
}) => {
  const [name, setName] = useState(currentName);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(currentName);
      setError(null);
    }
  }, [open, currentName]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Folder name is required.');
      return;
    }
    if (trimmed.includes('/') || trimmed.includes('\\')) {
      setError('Folder name cannot contain slashes.');
      return;
    }
    if (trimmed === currentName) {
      onClose();
      return;
    }
    setError(null);
    try {
      await onRename(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename folder.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => (!value ? onClose() : undefined)}>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Rename Folder</DialogTitle>
          </DialogHeader>

          <div className="space-y-2 py-4">
            <Label htmlFor="rename-folder">New Name</Label>
            <Input
              id="rename-folder"
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Renaming...' : 'Rename'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
